import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";

// UI Components
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// Icons
import {
  User,
  MapPin,
  Calendar,
  Heart,
  Users,
  Bookmark,
  Grid3X3,
  Play,
  Settings,
  Wand2,
  ExternalLink,
  ChevronRight
} from "lucide-react";

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [profileRes, videosRes] = await Promise.all([
        api.get('/abraham/profile'),
        api.get('/videos?status=completed&limit=30')
      ]);
      
      setProfile(profileRes.data);
      setVideos(videosRes.data);
    } catch (error) {
      console.error('Failed to fetch profile data:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto animate-fade-in" data-testid="profile-loading">
        <div className="flex flex-col md:flex-row gap-8 items-center md:items-start mb-12">
          <Skeleton className="w-32 h-32 md:w-40 md:h-40 rounded-full" />
          <div className="flex-1 space-y-4 text-center md:text-left">
            <Skeleton className="h-8 w-48 mx-auto md:mx-0" />
            <Skeleton className="h-4 w-32 mx-auto md:mx-0" />
            <div className="flex gap-8 justify-center md:justify-start">
              <Skeleton className="h-6 w-16" />
              <Skeleton className="h-6 w-16" />
              <Skeleton className="h-6 w-16" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-1 md:gap-4">
          {[...Array(9)].map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-md" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto animate-fade-in" data-testid="profile-page">
      {/* Profile Header - Instagram Style */}
      <div className="flex flex-col md:flex-row gap-8 items-center md:items-start mb-8">
        {/* Avatar */}
        <div className="relative">
          {profile?.portrait_url ? (
            <img 
              src={`${process.env.REACT_APP_BACKEND_URL}${profile.portrait_url}`}
              alt="Abraham"
              className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover avatar-patriarch"
              data-testid="profile-avatar"
            />
          ) : (
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-muted flex items-center justify-center avatar-patriarch">
              <User className="w-16 h-16 text-muted-foreground" />
            </div>
          )}
          {!profile?.portrait_url && (
            <Link 
              to="/generate"
              className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full gold-gradient flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
              data-testid="generate-portrait-btn"
            >
              <Wand2 className="w-5 h-5 text-white" />
            </Link>
          )}
        </div>

        {/* Profile Info */}
        <div className="flex-1 text-center md:text-left">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
            <h1 className="text-2xl font-display font-semibold" data-testid="profile-name">
              {profile?.name || "Abraham"}
            </h1>
            <div className="flex gap-2 justify-center md:justify-start">
              <Link to="/generate">
                <Button variant="outline" size="sm" className="gap-2" data-testid="edit-profile-btn">
                  <Settings className="w-4 h-4" />
                  Edit Profile
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-8 justify-center md:justify-start mb-4" data-testid="profile-stats">
            <div className="text-center md:text-left">
              <span className="font-display font-bold">{videos.length}</span>
              <span className="text-muted-foreground ml-1">posts</span>
            </div>
            <div className="text-center md:text-left">
              <span className="font-display font-bold">{profile?.followers?.toLocaleString() || 0}</span>
              <span className="text-muted-foreground ml-1">followers</span>
            </div>
            <div className="text-center md:text-left">
              <span className="font-display font-bold">{profile?.following || 3}</span>
              <span className="text-muted-foreground ml-1">following</span>
            </div>
          </div>

          {/* Bio */}
          <div className="space-y-2">
            <p className="font-display font-semibold">{profile?.title || "Father of Nations"}</p>
            <p className="text-sm text-muted-foreground max-w-lg" data-testid="profile-bio">
              {profile?.bio || "The patriarch who walked with God."}
            </p>
            <div className="flex flex-wrap gap-3 justify-center md:justify-start text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {profile?.birth_place || "Ur of the Chaldeans"}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {profile?.birth_year} - {profile?.death_year}
              </span>
            </div>
          </div>

          {/* Key Events */}
          <div className="mt-4 flex flex-wrap gap-2 justify-center md:justify-start">
            {profile?.key_events?.slice(0, 4).map((event, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {event.length > 25 ? event.substring(0, 25) + '...' : event}
              </Badge>
            ))}
            {profile?.key_events?.length > 4 && (
              <Badge variant="outline" className="text-xs">
                +{profile.key_events.length - 4} more
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Story Highlights */}
      <div className="mb-8 overflow-x-auto">
        <div className="flex gap-4 pb-2">
          {["The Call", "Journey", "Covenant", "Isaac", "Faith"].map((highlight, index) => (
            <div key={index} className="flex flex-col items-center gap-2 min-w-[80px]">
              <div className="w-16 h-16 rounded-full p-0.5 gold-gradient">
                <div className="w-full h-full rounded-full bg-background flex items-center justify-center">
                  <span className="text-2xl">{["🌟", "🐪", "📜", "👶", "🙏"][index]}</span>
                </div>
              </div>
              <span className="text-xs text-muted-foreground">{highlight}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Content Tabs */}
      <Tabs defaultValue="posts" className="w-full">
        <TabsList className="w-full justify-center border-b rounded-none bg-transparent h-auto p-0 mb-4">
          <TabsTrigger 
            value="posts" 
            className="flex items-center gap-2 rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent px-6 py-3"
            data-testid="tab-posts"
          >
            <Grid3X3 className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wider">Posts</span>
          </TabsTrigger>
          <TabsTrigger 
            value="videos" 
            className="flex items-center gap-2 rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent px-6 py-3"
            data-testid="tab-videos"
          >
            <Play className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wider">Videos</span>
          </TabsTrigger>
          <TabsTrigger 
            value="saved" 
            className="flex items-center gap-2 rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent px-6 py-3"
            data-testid="tab-saved"
          >
            <Bookmark className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wider">Saved</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="posts" className="mt-0" data-testid="posts-content">
          {videos.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 rounded-full border-2 border-dashed border-muted-foreground/30 flex items-center justify-center mx-auto mb-4">
                <Grid3X3 className="w-10 h-10 text-muted-foreground/50" />
              </div>
              <h3 className="font-display text-xl font-semibold mb-2">No Posts Yet</h3>
              <p className="text-muted-foreground mb-4">
                Generate videos to see them appear here
              </p>
              <Link to="/generate">
                <Button className="btn-primary gap-2" data-testid="create-first-post">
                  <Wand2 className="w-4 h-4" />
                  Create First Post
                </Button>
              </Link>
            </div>
          ) : (
            <div className="instagram-grid" data-testid="posts-grid">
              {videos.map((video) => (
                <Dialog key={video.id}>
                  <DialogTrigger asChild>
                    <div 
                      className="instagram-grid-item bg-muted cursor-pointer"
                      onClick={() => setSelectedVideo(video)}
                      data-testid={`post-${video.day_number}`}
                    >
                      {video.thumbnail_url ? (
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${video.thumbnail_url}`}
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-accent/10">
                          <span className="font-display text-3xl font-bold text-accent/50">
                            {video.day_number}
                          </span>
                        </div>
                      )}
                      <div className="instagram-overlay">
                        <div className="flex items-center gap-4 text-white">
                          <span className="flex items-center gap-1">
                            <Heart className="w-5 h-5 fill-current" />
                            {Math.floor(Math.random() * 1000)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-5 h-5" />
                            {Math.floor(Math.random() * 100)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl">
                    <DialogHeader>
                      <DialogTitle className="font-display">{video.title}</DialogTitle>
                      <DialogDescription>Day {video.day_number} of Abraham's Journey</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      {video.video_url && (
                        <video 
                          controls 
                          className="w-full rounded-lg"
                          src={`${process.env.REACT_APP_BACKEND_URL}${video.video_url}`}
                        />
                      )}
                      {video.opening_message && (
                        <div className="p-4 rounded-lg bg-accent/10 border border-accent/20">
                          <p className="text-sm font-medium text-accent">Daily Wisdom</p>
                          <p className="mt-1 font-display italic">{video.opening_message}</p>
                        </div>
                      )}
                      <p className="text-muted-foreground text-sm whitespace-pre-wrap">
                        {video.script_content?.substring(0, 500)}...
                      </p>
                      <Link to={`/scripts/${video.day_number}`}>
                        <Button variant="outline" className="gap-2">
                          View Full Script
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </DialogContent>
                </Dialog>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="videos" className="mt-0" data-testid="videos-content">
          <div className="text-center py-16 text-muted-foreground">
            <Play className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>Video gallery coming soon</p>
          </div>
        </TabsContent>

        <TabsContent value="saved" className="mt-0" data-testid="saved-content">
          <div className="text-center py-16 text-muted-foreground">
            <Bookmark className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p>Saved content coming soon</p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Profile;
